import React from 'react';
import { ArrowLeft, ArrowRight, Download, FileSpreadsheet, Database, CheckCircle2 } from 'lucide-react';
import styles from './ExportWizard.module.css';
import GoogleSheetsConfig from './GoogleSheetsConfig';

const ExportWizard = () => {
  const [currentStep, setCurrentStep] = React.useState(1);
  const [dataType, setDataType] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [fileFormat, setFileFormat] = React.useState('xlsx');
  const [googleSheetsConfig, setGoogleSheetsConfig] = React.useState({
    spreadsheetId: '',
    sheetName: '',
    isShared: false
  });
  const [dateRange, setDateRange] = React.useState({ start: '2025-01-15', end: '2025-04-15' });
  const [filters, setFilters] = React.useState({ status: 'all' });
  const [columns, setColumns] = React.useState([]);
  const [selectedColumns, setSelectedColumns] = React.useState([]);
  const [previewData, setPreviewData] = React.useState([]);
  const [exportStatus, setExportStatus] = React.useState('idle');
  const [totalSteps] = React.useState(5);
  const [appendData, setAppendData] = React.useState(false);
  const [expandedFormat, setExpandedFormat] = React.useState('');
  const googleSheetsConfigRef = React.useRef();
  
  const dataTypes = [
    { id: 'gl_report', name: 'GL Report' }
  ];
  
  const qboFields = {
    customers: ['Name', 'Company', 'Email', 'Phone', 'Address', 'City', 'State', 'Zip', 'Country', 'Created Date', 'Balance'],
    vendors: ['Name', 'Company', 'Email', 'Phone', 'Address', 'Tax ID', 'Account Number', 'Terms', 'Balance'],
    products: ['Name', 'SKU', 'Description', 'Price', 'Cost', 'Type', 'Category', 'Taxable', 'Income Account', 'Inventory Asset Account'],
    invoices: ['Customer', 'Invoice Number', 'Date', 'Due Date', 'Items', 'Amount', 'Notes', 'Status', 'Terms'],
    expenses: ['Vendor', 'Date', 'Category', 'Amount', 'Description', 'Payment Method', 'Reference Number', 'Billable'],
    bills: ['Vendor', 'Bill Number', 'Date', 'Due Date', 'Amount', 'Category', 'Status', 'Terms']
  };

  // File format options
  const fileFormats = [
    // { id: 'xlsx', name: 'Excel (.xlsx)', description: 'Microsoft Excel Workbook' },
    // { id: 'csv', name: 'CSV (.csv)', description: 'Comma Separated Values' },
    { id: 'sheets', name: 'Google Sheets', description: 'Export directly to Google Sheets' }
  ];
  
  // Generate sample data based on data type
  const generateSampleData = (type, count = 10) => {
    if (!type || !qboFields[type]) return [];
    
    const data = [];
    
    for (let i = 0; i < count; i++) {
      const item = {};
      
      qboFields[type].forEach(field => {
        if (field.includes('Date')) {
          const date = new Date();
          date.setDate(date.getDate() - Math.floor(Math.random() * 60));
          item[field] = date.toISOString().split('T')[0];
        } else if (field.includes('Amount') || field.includes('Price') || field.includes('Cost') || field.includes('Balance')) {
          item[field] = `$${(Math.random() * 1000).toFixed(2)}`;
        } else if (field === 'Status') {
          item[field] = ['Paid', 'Unpaid', 'Overdue', 'Pending'][Math.floor(Math.random() * 4)];
        } else if (field === 'Customer' || field === 'Vendor') {
          item[field] = `Sample ${field} ${i + 1}`;
        } else {
          item[field] = `Sample ${field} ${i + 1}`;
        }
      });
      
      data.push(item);
    }
    
    return data;
  };
  
  // Effect to update columns when data type changes
  React.useEffect(() => {
    if (dataType && qboFields[dataType]) {
      const allColumns = qboFields[dataType].map(field => ({
        id: field,
        name: field,
        selected: true
      }));
      
      setColumns(allColumns);
      setSelectedColumns(allColumns.map(col => col.id));
      
      // Generate sample data for preview
      const sampleData = generateSampleData(dataType);
      setPreviewData(sampleData);
      
      // Set default filename
      setFileName(`${dataType}_export`);
    }
  }, [dataType, generateSampleData, qboFields]);
  
  
  // Toggle column selection
  const toggleColumn = (columnId) => {
    if (selectedColumns.includes(columnId)) {
      setSelectedColumns(selectedColumns.filter(id => id !== columnId));
    } else {
      setSelectedColumns([...selectedColumns, columnId]);
    }
  };
  
  // Toggle all columns
  const toggleAllColumns = (selectAll) => {
    if (selectAll) {
      setSelectedColumns(columns.map(col => col.id));
    } else {
      setSelectedColumns([]);
    }
  };
  
  // Mock export function
  const performExport = () => {
    setExportStatus('exporting');
    
    // Simulate export process
    setTimeout(() => {
      setExportStatus('success');
    }, 2000);
  };

  const nextStep = () => {
    if (fileFormat === 'sheets' && currentStep === 2) {
      const isValid = googleSheetsConfigRef.current?.handleValidation();
      if (!isValid) {
        return; // Do not proceed if validation fails
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };
  
  const prevStep = () => {
    // Normal regression
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  
  // Determine if current step is complete
  const isStepComplete = () => {
    switch (currentStep) {
      case 1: return dataType !== '';
      case 2: return (fileFormat !== '' && fileName !== '') || fileFormat === 'sheets';
      case 3: return true; // Filters are optional
      case 4: return true;
      default: return false;
    }
  };
  
  // Get the actual step for rendering based on current step and file format
  const getDisplayStep = () => {
    if (fileFormat !== 'sheets') {
      // For Excel and CSV: 1-Type, 2-Format, 3-Filters, 4-Columns, 5-Export
      if (currentStep >= 3) {
        return currentStep + 1;
      }
      return currentStep;
    }
    // For Google Sheets: 1-Type, 2-Format, 3-Sheets Config, 4-Filters, 5-Columns, 6-Export
    return currentStep;
  };
  
  // Render the wizard content based on current step
  const renderStepContent = () => {
    const displayStep = getDisplayStep();
    
    switch (displayStep) {
      case 1:
        return (
          <div className={styles['step-container']}>
            <h2 className={styles['step-title']}>Select Data Type to Export</h2>
            <p className={styles['step-description']}>Choose the type of data you want to export from QuickBooks Online.</p>
            <div className={styles['data-type-grid']}>
              {dataTypes.map(type => (
                <div 
                  key={type.id}
                  className={`${styles['data-type-card']} ${dataType === type.id ? styles['selected'] : ''}`}
                  onClick={() => setDataType(type.id)}
                >
                  <Database className={`${styles['data-type-icon']} ${dataType === type.id ? styles['selected'] : ''}`} />
                  <h3 className={styles['data-type-name']}>{type.name}</h3>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className={styles['step-container']}>
            <h2 className={styles['step-title']}>Choose Export Format</h2>
            <p className={styles['step-description']}>Select a file format and specify options for your export.</p>
            
            <div className={styles['format-options']}>
              {fileFormats.map(format => (
                <div 
                  key={format.id}
                  className={`${styles['format-card']} ${fileFormat === format.id ? styles['selected'] : ''} ${expandedFormat === format.id ? styles['expanded'] : ''}`}
                  onClick={() => {
                    setFileFormat(format.id);
                    setExpandedFormat(format.id);
                  }}
                >
                  <div className={styles['format-header']}>
                    <FileSpreadsheet className={`${styles['format-icon']} ${fileFormat === format.id ? styles['selected'] : ''}`} />
                    <div>
                      <div className={`${styles['format-name']} ${fileFormat === format.id ? styles['selected'] : ''}`}>{format.name}</div>
                      <div className={styles['format-description']}>{format.description}</div>
                    </div>
                  </div>
                  
                  {expandedFormat === format.id && format.id === 'sheets' && (
                    <GoogleSheetsConfig
                      googleSheetsConfig={googleSheetsConfig}
                      setGoogleSheetsConfig={setGoogleSheetsConfig}
                      appendData={appendData}
                      setAppendData={setAppendData}
                      ref={googleSheetsConfigRef}
                      onValidate={(config) => setGoogleSheetsConfig(config)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className={styles['step-container']}>
            <h2 className={styles['step-title']}>Set Filters</h2>
            <p className={styles['step-description']}>Filter the data you want to export. All filters are optional.</p>
            
            <div className={styles['filters-container']}>
              {/* Date range filter - only for transaction types */}
              {['invoices', 'expenses', 'bills'].includes(dataType) && (
                <div className={styles['filter-card']}>
                  <h3 className={styles['filter-title']}>Date Range</h3>
                  <div className={styles['date-range-container']}>
                    <div className={styles['date-field']}>
                      <label className={styles['date-label']}>Start Date</label>
                      <input 
                        type="date" 
                        className={styles['date-input']}
                        value={dateRange.start}
                        onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                      />
                    </div>
                    <div className={styles['date-field']}>
                      <label className={styles['date-label']}>End Date</label>
                      <input 
                        type="date" 
                        className={styles['date-input']}
                        value={dateRange.end}
                        onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Status filter for invoices and bills */}
              {(dataType === 'invoices' || dataType === 'bills') && (
                <div className={styles['filter-card']}>
                  <h3 className={styles['filter-title']}>Status</h3>
                  <select 
                    className={styles['filter-select']}
                    value={filters.status || 'all'}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                  >
                    <option value="all">All Statuses</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="overdue">Overdue</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className={styles['step-container']}>
            <h2 className={styles['step-title']}>Select Columns</h2>
            <p className={styles['step-description']}>Choose which columns to include in your export.</p>
            
            <div className={styles['columns-header']}>
              <div className={styles['column-actions']}>
                <button 
                  className={styles['column-action-btn']}
                  onClick={() => toggleAllColumns(true)}
                >
                  Select All
                </button>
                <button 
                  className={styles['column-action-btn']}
                  onClick={() => toggleAllColumns(false)}
                >
                  Deselect All
                </button>
              </div>
              <div className={styles['column-counter']}>
                {selectedColumns.length} of {columns.length} columns selected
              </div>
            </div>
            
            <div className={styles['columns-table-container']}>
              <table className={styles['columns-table']}>
                <thead>
                  <tr>
                    <th className={styles['column-header']}>Include</th>
                    <th className={styles['column-header']}>Column Name</th>
                    <th className={styles['column-header']}>Sample Data</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map(column => (
                    <tr key={column.id} className={styles['column-row']}>
                      <td className={styles['column-cell']}>
                        <input 
                          type="checkbox" 
                          className={styles['column-checkbox']}
                          checked={selectedColumns.includes(column.id)}
                          onChange={() => toggleColumn(column.id)}
                        />
                      </td>
                      <td className={styles['column-cell']}>
                        {column.name}
                      </td>
                      <td className={`${styles['column-cell']} ${styles['sample-data']}`}>
                        {previewData.length > 0 ? previewData[0][column.id] : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 5:
        return (
          <div className={`${styles['step-container']} ${styles['export-step']}`}>
            <div className={styles['export-content']}>
              {exportStatus === 'idle' && (
                <>
                  <h2 className={styles['step-title']}>Preview Export</h2>
                  <p className={styles['step-description']}>
                    You're about to export {previewData.length} {dataTypes.find(t => t.id === dataType)?.name.toLowerCase()} from QuickBooks Online.
                  </p>
                </>
              )}
              
              {exportStatus === 'exporting' && (
                <>
                  <div className={styles['spinner-container']}>
                    <div className={styles['spinner']}></div>
                  </div>
                  <h2 className={styles['step-title']}>Exporting Data</h2>
                  <p className={styles['step-description']}>This may take a few moments...</p>
                </>
              )}
              
              {exportStatus === 'success' && (
                <>
                  <CheckCircle2 className={styles['success-icon']} />
                  <h2 className={styles['step-title']}>Export Complete!</h2>
                  <p className={styles['step-description']}>
                    Successfully exported {previewData.length} {dataTypes.find(t => t.id === dataType)?.name.toLowerCase()} from QuickBooks Online.
                  </p>
                  <div className={styles['result-card']}>
                    <h3 className={styles['result-title']}>Export Results</h3>
                    {fileFormat !== 'sheets' ? (
                      <p><span className={styles['result-label']}>File:</span> {fileName}.{fileFormat}</p>
                    ) : (
                      <>
                        <p><span className={styles['result-label']}>Google Spreadsheet:</span> {googleSheetsConfig.spreadsheetId}</p>
                        <p><span className={styles['result-label']}>Sheet:</span> {googleSheetsConfig.sheetName}</p>
                      </>
                    )}
                    <p><span className={styles['result-label']}>Records exported:</span> {previewData.length}</p>
                    <p><span className={styles['result-label']}>Columns included:</span> {selectedColumns.length}</p>
                  </div>
                </>
              )}
            </div>
            
            {exportStatus === 'idle' && (
              <div className={styles['preview-container']}>
                <h3 className={styles['preview-title']}>Export Preview</h3>
                <div className={styles['preview-table-container']}>
                  <table className={styles['preview-table']}>
                    <thead>
                      <tr>
                        {selectedColumns.map(column => (
                          <th key={column} className={styles['preview-header']}>
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 5).map((row, rowIndex) => (
                        <tr key={rowIndex} className={`${styles['preview-row']} ${rowIndex % 2 === 0 ? styles['even'] : styles['odd']}`}>
                          {selectedColumns.map(column => (
                            <td key={column} className={styles['preview-cell']}>
                              {row[column]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {previewData.length > 5 && (
                  <p className={styles['preview-note']}>Showing 5 of {previewData.length} records</p>
                )}
              </div>
            )}
            
            {exportStatus === 'idle' && (
              <div className={styles['summary-container']}>
                <h3 className={styles['summary-title']}>Export Summary</h3>
                <div className={styles['summary-card']}>
                  <p><span className={styles['summary-label']}>Data Type:</span> {dataTypes.find(t => t.id === dataType)?.name}</p>
                  <p><span className={styles['summary-label']}>File Format:</span> {fileFormats.find(f => f.id === fileFormat)?.name}</p>
                  {fileFormat !== 'sheets' ? (
                    <p><span className={styles['summary-label']}>Filename:</span> {fileName}.{fileFormat}</p>
                  ) : (
                    <>
                      <p><span className={styles['summary-label']}>Google Spreadsheet ID:</span> {googleSheetsConfig.spreadsheetId}</p>
                      <p><span className={styles['summary-label']}>Sheet Name:</span> {googleSheetsConfig.sheetName}</p>
                    </>
                  )}
                  <p><span className={styles['summary-label']}>Records to export:</span> {previewData.length}</p>
                  <p><span className={styles['summary-label']}>Columns:</span> {selectedColumns.length} of {columns.length}</p>
                  
                  {/* Show applied filters */}
                  {(['invoices', 'expenses', 'bills'].includes(dataType) && (dateRange.start || dateRange.end)) && (
                    <p>
                      <span className={styles['summary-label']}>Date Range:</span> {dateRange.start || 'Any'} to {dateRange.end || 'Any'}
                    </p>
                  )}
                  
                  {((dataType === 'invoices' || dataType === 'bills') && filters.status) && (
                    <p>
                      <span className={styles['summary-label']}>Status:</span> {filters.status === 'all' ? 'All Statuses' : filters.status}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {exportStatus === 'idle' && (
              <button
                className={styles['export-button']}
                onClick={performExport}
              >
                <Download className={styles['button-icon']} />
                {fileFormat === 'sheets' ? 'Export to Google Sheets' : `Export ${fileFormat.toUpperCase()}`}
              </button>
            )}
            
            {exportStatus === 'success' && (
              <div className={styles['success-actions']}>
                <button
                  className={styles['secondary-button']}
                  onClick={() => {
                    setCurrentStep(1);
                    setDataType('');
                    setFileName('');
                    setFileFormat('xlsx');
                    setDateRange({ start: '', end: '' });
                    setFilters({});
                    setColumns([]);
                    setSelectedColumns([]);
                    setPreviewData([]);
                    setExportStatus('idle');
                  }}
                >
                  Export More Data
                </button>
                <button className={styles['primary-button']}>
                  View in QuickBooks
                </button>
              </div>
            )}
          </div>
        );
      
      default:
        return <div>Unknown step</div>;
    }
  };
  
  return (
    <div className={styles['export-wizard']}> {/* Apply the main container class */}
      {/* Header with progress indicator */}
      <div className={styles['wizard-header']}> {/* Apply the header class */}
        <h1 className={styles['wizard-title']}>Export Data from QuickBooks Online</h1>
        <div className={styles['progress-container']}>
          {[1, 2, 3, 4, 5].map((step) => (
            <React.Fragment key={step}>
              <div 
                className={`${styles['progress-step']} ${
                  currentStep === step 
                    ? styles['current'] 
                    : currentStep > step 
                      ? styles['completed'] 
                      : styles['pending']
                }`}
              >
                {currentStep > step ? (
                  <CheckCircle2 className={styles['step-icon']} />
                ) : (
                  step
                )}
              </div>
              
              {step < 5 && (
                <div 
                  className={`${styles['progress-line']} ${
                    currentStep > step ? styles['completed'] : styles['pending']
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className={styles['progress-labels']}>
          <span className={styles['progress-label']}>Type</span>
          <span className={styles['progress-label']}>Format</span>
          <span className={styles['progress-label']}>Filter</span>
          <span className={styles['progress-label']}>Columns</span>
          <span className={styles['progress-label']}>Export</span>
        </div>
      </div>
      
      {/* Main content */}
      <div className={styles['wizard-content']}> {/* Apply the content class */}
        {renderStepContent()}
      </div>
      
      {/* Footer with navigation buttons */}
      <div className={styles['wizard-footer']}> {/* Apply the footer class */}
        <button
          className={styles['back-button']}
          onClick={prevStep}
          disabled={currentStep === 1 || exportStatus === 'exporting'}
        >
          <ArrowLeft className={styles['button-icon']} />
          Back
        </button>
        
        {currentStep < 5 && (
          <button
            className={styles['next-button']}
            onClick={nextStep}
            disabled={!isStepComplete() || exportStatus === 'exporting'}
          >
            Next
            <ArrowRight className={styles['button-icon']} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ExportWizard;